import { useState, useEffect, useCallback } from 'react';
import { IToken } from '../contexts/ChainConfigContext';
import { normalizeAddress } from '../utils/tokenValidation';

const CUSTOM_TOKENS_STORAGE_KEY = 'customTokens';

export interface CustomTokensStorage {
  [chainId: number]: IToken[];
}

export interface UseCustomTokensReturn {
  customTokens: CustomTokensStorage;
  addCustomToken: (chainId: number, token: IToken) => void;
  removeCustomToken: (chainId: number, tokenAddress: string) => void;
  getCustomTokensForChain: (chainId: number) => IToken[];
  isCustomToken: (chainId: number, tokenAddress: string) => boolean;
  clearCustomTokensForChain: (chainId: number) => void;
}

export const useCustomTokens = (): UseCustomTokensReturn => {
  const [customTokens, setCustomTokens] = useState<CustomTokensStorage>({});

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(CUSTOM_TOKENS_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          setCustomTokens(parsed);
        }
      } catch (error) {
        console.error('Failed to load custom tokens from localStorage:', error);
      }
    }
  }, []);

  const saveToStorage = useCallback((tokens: CustomTokensStorage) => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(CUSTOM_TOKENS_STORAGE_KEY, JSON.stringify(tokens));
      } catch (error) {
        console.error('Failed to save custom tokens to localStorage:', error);
      }
    }
  }, []);

  const addCustomToken = useCallback((chainId: number, token: IToken) => {
    const normalizedAddress = normalizeAddress(token.address);
    const normalizedToken = { ...token, address: normalizedAddress };

    setCustomTokens(prev => {
      const chainTokens = prev[chainId] || [];
      
      const existingIndex = chainTokens.findIndex(
        t => t.address.toLowerCase() === normalizedAddress.toLowerCase()
      );

      let newChainTokens;
      if (existingIndex >= 0) {
        newChainTokens = [...chainTokens];
        newChainTokens[existingIndex] = normalizedToken;
      } else {
        newChainTokens = [...chainTokens, normalizedToken];
      }

      const newCustomTokens = {
        ...prev,
        [chainId]: newChainTokens,
      };

      saveToStorage(newCustomTokens);
      return newCustomTokens;
    });
  }, [saveToStorage]);

  const removeCustomToken = useCallback((chainId: number, tokenAddress: string) => {
    const normalizedAddress = normalizeAddress(tokenAddress);

    setCustomTokens(prev => {
      const chainTokens = prev[chainId] || [];
      const newChainTokens = chainTokens.filter(
        t => t.address.toLowerCase() !== normalizedAddress.toLowerCase()
      );

      const newCustomTokens = {
        ...prev,
        [chainId]: newChainTokens,
      };

      saveToStorage(newCustomTokens);
      return newCustomTokens;
    });
  }, [saveToStorage]);

  const getCustomTokensForChain = useCallback((chainId: number): IToken[] => {
    return customTokens[chainId] || [];
  }, [customTokens]);

  const isCustomToken = useCallback((chainId: number, tokenAddress: string): boolean => {
    const chainTokens = customTokens[chainId] || [];
    const normalizedAddress = normalizeAddress(tokenAddress);
    return chainTokens.some(
      t => t.address.toLowerCase() === normalizedAddress.toLowerCase()
    );
  }, [customTokens]);

  const clearCustomTokensForChain = useCallback((chainId: number) => {
    setCustomTokens(prev => {
      const newCustomTokens = { ...prev };
      delete newCustomTokens[chainId];
      saveToStorage(newCustomTokens);
      return newCustomTokens;
    });
  }, [saveToStorage]);

  return {
    customTokens,
    addCustomToken,
    removeCustomToken,
    getCustomTokensForChain,
    isCustomToken,
    clearCustomTokensForChain,
  };
}; 